import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import {
	CalendarIcon,
	ClockIcon,
	CubeIcon,
	UserGroupIcon,
	PencilIcon,
	TrashIcon,
} from '@heroicons/react/24/outline';

import { getReservations, deleteReservation } from '../../api/reservations';

import Badge from '../../components/Badge';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import DataTable from '../../components/DataTable';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import usePrevious from '../../hooks/usePrevious';
import {
	classNames,
	getUserFullName,
} from '../../components/helpers/functions';
import { AssetBookingManager } from '../../components/Booking/AssetBookingManager';

const getStatusBadge = (startTime, endTime) => {
	const now = moment();
	const start = moment(startTime);
	const end = moment(endTime);

	if (now.isBefore(start)) {
		return { status: 'info', label: 'Upcoming' };
	} else if (now.isAfter(end)) {
		return { status: 'success', label: 'Completed' };
	} else {
		return { status: 'warning', label: 'In Progress' };
	}
};

export const Bookings = () => {
	const navigate = useNavigate();
	const { id, tab } = useParams();
	const [currentTab, setCurrentTab] = useState(null);

	const { open: bookingOverviewOpen, setOpen: openBookingOverview } =
		useModal('bookingOverview');

	const { open: addBookingOpen, setOpen: openAddBooking } =
		useModal('addBooking');

	const { setClose: closeDeleteModal, setOpen: openDeleteModal } =
		useModal('deleteModal');

	const { value: reservations, execute: refreshReservations } = useAsync(
		getReservations,
		{ defaultPayload: {} },
	);

	const { execute: onDeleteReservation } = useAsync(deleteReservation, {
		immediate: false,
	});

	// Refresh when booking overview closes
	const prevBookingOverviewOpen = usePrevious(bookingOverviewOpen);
	useEffect(() => {
		if (!bookingOverviewOpen && prevBookingOverviewOpen) {
			refreshReservations();
		}
	}, [bookingOverviewOpen]);

	// Refresh when navigating away from a specific booking (e.g., after delete)
	const prevId = usePrevious(id);
	useEffect(() => {
		if (!id && prevId) {
			refreshReservations();
		}
	}, [id]);

	// Refresh when add booking modal closes
	const prevAddBookingOpen = usePrevious(addBookingOpen);
	useEffect(() => {
		if (!addBookingOpen && prevAddBookingOpen) {
			refreshReservations();
		}
	}, [addBookingOpen]);

	// Open modal when ID is in URL
	useEffect(() => {
		if (id) {
			openBookingOverview({
				id,
				path: '/bookings',
			});
		}
	}, [id]);

	// Filter reservations based on tab
	const filterReservations = (items) => {
		if (!items) return [];
		const now = moment();

		return items.filter((reservation) => {
			const start = moment(reservation.startTime);
			const end = moment(reservation.endTime);

			switch (currentTab) {
				case 'upcoming':
					return now.isBefore(start);
				case 'in-progress':
					return now.isAfter(start) && now.isBefore(end);
				case 'completed':
					return now.isAfter(end);
				default:
					return true;
			}
		});
	};

	// Get counts for tabs
	const getCounts = (items) => {
		if (!items) return { upcoming: 0, inProgress: 0, completed: 0 };
		const now = moment();

		return items.reduce(
			(acc, reservation) => {
				const start = moment(reservation.startTime);
				const end = moment(reservation.endTime);

				if (now.isBefore(start)) {
					acc.upcoming++;
				} else if (now.isAfter(end)) {
					acc.completed++;
				} else {
					acc.inProgress++;
				}
				return acc;
			},
			{ upcoming: 0, inProgress: 0, completed: 0 },
		);
	};

	const counts = getCounts(reservations);

	const handleTabChange = (tab) => {
		setCurrentTab(tab);
		if (tab) {
			navigate(`/bookings?tab=${tab}`);
		} else {
			navigate('/bookings');
		}
	};

	const handleDelete = (reservation) => {
		openDeleteModal({
			title: `Delete Booking`,
			description: `Are you sure you want to delete this booking for ${moment(reservation.startTime).format('MMM D, YYYY')}? This action cannot be undone.`,
			confirmText: 'Delete Booking',
			onConfirm: () =>
				onDeleteReservation({ id: reservation.id }, () => {
					refreshReservations();
					closeDeleteModal();
				}),
		});
	};

	const tabs = [
		{
			name: 'All',
			onClick: () => handleTabChange(null),
			current: !currentTab,
			count: reservations?.length || 0,
		},
		{
			name: 'Upcoming',
			onClick: () => handleTabChange('upcoming'),
			current: currentTab === 'upcoming',
			count: counts.upcoming,
		},
		{
			name: 'In Progress',
			onClick: () => handleTabChange('in-progress'),
			current: currentTab === 'in-progress',
			count: counts.inProgress,
		},
		{
			name: 'Completed',
			onClick: () => handleTabChange('completed'),
			current: currentTab === 'completed',
			count: counts.completed,
		},
	];

	console.log('reservations: ', reservations);

	return (
		<MainContent maxWidth="max-w-full">
			<PageHeading
				actions={[
					{
						name: 'New Booking',
						onClick: () => openAddBooking(true),
					},
				]}
				divider={false}
			>
				Bookings
			</PageHeading>

			{/* Timeline Booking Component */}
			<div className="mb-6">
				<AssetBookingManager onBookingChange={refreshReservations} />
			</div>

			{/* Stats Cards */}
			<div className="grid grid-cols-1 gap-5 sm:grid-cols-4 mb-6">
				<div className="bg-white overflow-hidden shadow rounded-lg">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<CalendarIcon className="h-6 w-6 text-gray-400" />
							</div>
							<div className="ml-5 w-0 flex-1">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate">
										Total Bookings
									</dt>
									<dd className="text-lg font-semibold text-gray-900">
										{reservations?.length || 0}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>
				<div className="bg-white overflow-hidden shadow rounded-lg">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<ClockIcon className="h-6 w-6 text-blue-400" />
							</div>
							<div className="ml-5 w-0 flex-1">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate">
										Upcoming
									</dt>
									<dd className="text-lg font-semibold text-blue-600">
										{counts.upcoming}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>
				<div className="bg-white overflow-hidden shadow rounded-lg">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<CubeIcon className="h-6 w-6 text-yellow-400" />
							</div>
							<div className="ml-5 w-0 flex-1">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate">
										In Progress
									</dt>
									<dd className="text-lg font-semibold text-yellow-600">
										{counts.inProgress}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>
				<div className="bg-white overflow-hidden shadow rounded-lg">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<UserGroupIcon className="h-6 w-6 text-green-400" />
							</div>
							<div className="ml-5 w-0 flex-1">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate">
										Completed
									</dt>
									<dd className="text-lg font-semibold text-green-600">
										{counts.completed}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Tabs */}
			<div className="grid grid-cols-12">
				<div className="col-span-2">
					<nav className="flex flex-1 flex-col mt-2" aria-label="Sidebar">
						<ul role="list" className="-mx-2 space-y-1">
							{tabs.map((item) => (
								<li key={item.name}>
									<div
										onClick={item.onClick}
										className={classNames(
											item.current
												? 'bg-gray-50 text-indigo-600'
												: 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50',
											'group flex gap-x-3 rounded-md p-2 text-xs leading-6 font-medium cursor-pointer',
										)}
									>
										{item.name}
										{typeof item.count !== 'undefined' ? (
											<span
												className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-white px-2.5 py-0.5 text-center text-xxs font-medium leading-5 text-gray-600 ring-1 ring-inset ring-gray-200"
												aria-hidden="true"
											>
												{item.count || 0}
											</span>
										) : null}
									</div>
								</li>
							))}
						</ul>
					</nav>
				</div>

				{/* Table */}
				<div className="col-span-10 mx-2 px-4 mt-4">
					<DataTable
						data={{
							rows: filterReservations(reservations),
						}}
						manualSorting={false}
						columns={[
							{
								header: 'Date & Time',
								accessorKey: 'startTime',
								cell: (info) => {
									const startTime = info.getValue();
									const endTime = info.row.original.endTime;
									return (
										<div>
											<div className="text-sm font-medium text-gray-900">
												{moment(startTime).format('MMM D, YYYY')}
											</div>
											<div className="text-xs text-gray-500">
												{moment(startTime).format('h:mm A')} -{' '}
												{moment(endTime).format('h:mm A')}
											</div>
										</div>
									);
								},
							},
							{
								header: 'Assets',
								accessorKey: 'assets',
								cell: (info) => {
									const assets = info.getValue() || [];
									return (
										<div className="flex items-center">
											<CubeIcon className="h-4 w-4 text-gray-400 mr-2" />
											<span className="text-sm text-gray-900">
												{assets.length} asset{assets.length !== 1 ? 's' : ''}
											</span>
										</div>
									);
								},
							},
							{
								header: 'Users',
								accessorKey: 'users',
								cell: (info) => {
									const users = info.getValue() || [];
									return (
										<div className="flex items-center">
											<UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
											<div>
												{users.slice(0, 2).map((user, i) => (
													<span key={user.id} className="text-sm text-gray-900">
														{getUserFullName(user)}
														{i < Math.min(users.length, 2) - 1 ? ', ' : ''}
													</span>
												))}
												{users.length > 2 && (
													<span className="text-sm text-gray-500">
														{' '}
														+{users.length - 2} more
													</span>
												)}
											</div>
										</div>
									);
								},
							},
							{
								header: 'Status',
								accessorKey: 'status',
								cell: (info) => {
									const { status, label } = getStatusBadge(
										info.row.original.startTime,
										info.row.original.endTime,
									);
									return (
										<Badge isBeta size="xxs" status={status}>
											{label}
										</Badge>
									);
								},
							},
							{
								header: 'Created',
								accessorKey: 'createdAt',
								cell: (info) => (
									<span className="text-sm text-gray-500">
										{moment(info.getValue()).fromNow()}
									</span>
								),
							},
							{
								header: '',
								accessorKey: 'actions',
								enableSorting: false,
								cell: (info) => {
									const reservation = info.row.original;
									return (
										<div className="flex items-center space-x-2">
											<button
												onClick={(e) => {
													e.stopPropagation();
													openAddBooking({ id: reservation.id, edit: true, path: '/bookings' });
												}}
												className="text-gray-400 hover:text-indigo-600"
											>
												<PencilIcon className="h-4 w-4" />
											</button>
											<button
												onClick={(e) => {
													e.stopPropagation();
													handleDelete(reservation);
												}}
												className="text-gray-400 hover:text-red-600"
											>
												<TrashIcon className="h-4 w-4" />
											</button>
										</div>
									);
								},
							},
						]}
						onRow={(row) => {
							navigate(`/bookings/${row.original.id}`);
						}}
						hasCheckboxes={false}
						usePagination={false}
						useExport={false}
						useSearch={true}
						disableTitle={true}
						initialState={{
							sortBy: [
								{
									id: 'startTime',
									desc: false,
								},
							],
						}}
					/>
				</div>
			</div>
		</MainContent>
	);
};

export default Bookings;
